import React, { useState } from "react";
import {
  Grid,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Tabs,
  Tab,
  Box,
  useMediaQuery,
  Chip,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
  Avatar,
  CircularProgress,
  Badge,
  Container,
  TextField,
  InputAdornment,
  Tooltip,
  TablePagination,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import RefreshIcon from "@mui/icons-material/Refresh";
import EventIcon from "@mui/icons-material/Event";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ScheduleIcon from "@mui/icons-material/Schedule";
import HistoryIcon from "@mui/icons-material/History";
import InfoIcon from "@mui/icons-material/Info";
import CBackDrop from "../../../basecomp/components/CBackDrop";
import ApproverDashboardVM from "../viewmodal/ApproverDashboardVM";

// Tab Panel Component
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 1, sm: 2 } }}>{children}</Box>
      )}
    </div>
  );
};

const ApproverDashboardView = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  
  // Local state for pagination and search
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");

  const {
    visitors,
    currentTab,
    loading,
    filterVisitors,
    handleTabChange,
    handleViewDetails,
    refreshData,
    tabCounts,
  } = ApproverDashboardVM();

  // Handle pagination changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter visitors based on search text
  const getFilteredVisitors = (tabIndex) => {
    const filteredByTab = filterVisitors(tabIndex);
    
    if (!searchText.trim()) {
      return filteredByTab;
    }
    
    const searchLower = searchText.toLowerCase();
    return filteredByTab.filter(
      visitor => 
        visitor.full_name?.toLowerCase().includes(searchLower) ||
        visitor.email?.toLowerCase().includes(searchLower) ||
        visitor.contact_number?.includes(searchLower) ||
        visitor.purpose?.toLowerCase().includes(searchLower)
    );
  };

  // Paginate the filtered visitors
  const getPaginatedVisitors = (tabIndex) => {
    const filtered = getFilteredVisitors(tabIndex);
    return filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  // Get icon based on status
  const getStatusIcon = (status) => {
    switch (status) {
      case "Approved":
        return <CheckCircleIcon fontSize="small" />;
      case "Rejected":
        return <CancelIcon fontSize="small" />;
      case "On Hold":
        return <HourglassEmptyIcon fontSize="small" />;
      case "Not Processed":
        return <ScheduleIcon fontSize="small" />;
      case "Resubmitted":
        return <RefreshIcon fontSize="small" />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };

  // Status chip with appropriate colors
  const StatusChip = ({ status }) => {
    let color, bgColor, icon;

    switch (status) {
      case "Approved":
        color = "#1b5e20";
        bgColor = "#e8f5e9";
        icon = <CheckCircleIcon fontSize="small" />;
        break;
      case "Rejected":
        color = "#b71c1c";
        bgColor = "#ffebee";
        icon = <CancelIcon fontSize="small" />;
        break;
      case "Not Processed":
        color = "#0d47a1";
        bgColor = "#e3f2fd";
        icon = <ScheduleIcon fontSize="small" />;
        break;
      case "On Hold":
        color = "#e65100";
        bgColor = "#fff3e0";
        icon = <HourglassEmptyIcon fontSize="small" />;
        break;
      case "Resubmitted":
        color = "#0277bd";
        bgColor = "#e1f5fe";
        icon = <RefreshIcon fontSize="small" />;
        break;
      default:
        color = "#424242";
        bgColor = "#f5f5f5";
        icon = <InfoIcon fontSize="small" />;
    }

    return (
      <Chip
        icon={icon}
        label={status}
        size={isMobile ? "small" : "medium"}
        sx={{ 
          fontWeight: 600,
          color: color,
          bgcolor: bgColor,
          "& .MuiChip-icon": {
            color: color
          }
        }}
      />
    );
  };

  // Mobile visitor card component with expandable details
  const VisitorCard = ({ visitor }) => {
    const [expanded, setExpanded] = React.useState(false);

    const toggleExpand = () => {
      setExpanded(!expanded);
    };

    return (
      <Paper 
        elevation={1} 
        sx={{ 
          mb: 2, 
          overflow: "hidden", 
          borderRadius: 2,
          border: "1px solid rgba(0, 0, 0, 0.09)"
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <Avatar 
              sx={{ 
                bgcolor: visitor.status === "Approved" ? "success.light" :
                        visitor.status === "Rejected" ? "error.light" :
                        visitor.status === "On Hold" ? "warning.light" :
                        "info.light",
                mr: 1.5,
              }}
            >
              {visitor.full_name ? visitor.full_name.charAt(0).toUpperCase() : "V"}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {visitor.full_name}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                <CalendarTodayIcon
                  fontSize="small"
                  sx={{ mr: 0.75, color: "text.secondary", fontSize: "0.9rem" }}
                />
                <Typography variant="body2" color="text.secondary">
                  {new Date(visitor.visit_from).toLocaleDateString()} -{" "}
                  {new Date(visitor.visit_till).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
            <StatusChip status={visitor.status} />
          </Box>
          
          <Box 
            sx={{ 
              display: "flex", 
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <Button
              variant="contained"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => handleViewDetails(visitor)}
              sx={{ 
                borderRadius: 1.5,
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none"
              }}
            >
              View Details
            </Button>
            
            <IconButton
              onClick={toggleExpand}
              size="small"
              sx={{ 
                ml: 1,
                bgcolor: "rgba(0, 0, 0, 0.04)",
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.08)"
                }
              }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider />
          <List dense disablePadding>
            <ListItem>
              <ListItemText
                primary="Contact"
                secondary={
                  <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                    <PhoneIcon
                      fontSize="small"
                      sx={{ mr: 1, fontSize: "0.9rem", color: "primary.main" }}
                    />
                    {visitor.contact_number}
                  </Box>
                }
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                  fontWeight: 600
                }}
                secondaryTypographyProps={{ component: "div" }}
              />
            </ListItem>
            <Divider variant="inset" component="li" />
            <ListItem>
              <ListItemText
                primary="Email"
                secondary={
                  <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                    <EmailIcon
                      fontSize="small"
                      sx={{ mr: 1, fontSize: "0.9rem", color: "primary.main" }}
                    />
                    {visitor.email}
                  </Box>
                }
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                  fontWeight: 600
                }}
                secondaryTypographyProps={{ component: "div" }}
              />
            </ListItem>
            {visitor.purpose && (
              <>
                <Divider variant="inset" component="li" />
                <ListItem>
                  <ListItemText
                    primary="Purpose"
                    secondary={visitor.purpose}
                    primaryTypographyProps={{
                      variant: "body2",
                      color: "text.secondary",
                      fontWeight: 600
                    }}
                  />
                </ListItem>
              </>
            )}
          </List>
        </Collapse>
      </Paper>
    );
  };

  // Desktop table view
  const VisitorTable = ({ filteredVisitors }) => (
    <TableContainer 
      component={Paper} 
      elevation={0}
      sx={{ 
        borderRadius: 2,
        border: "1px solid rgba(0, 0, 0, 0.09)",
        overflow: "hidden"
      }}
    >
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: "#f8fafd" }}>
            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Visit Period</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredVisitors.length > 0 ? (
            filteredVisitors.map((visitor) => (
              <TableRow 
                key={visitor.id}
                sx={{ 
                  "&:hover": { 
                    bgcolor: "rgba(0, 0, 0, 0.04)" 
                  },
                  transition: "background-color 0.2s"
                }}
              >
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: visitor.status === "Approved" ? "success.light" :
                                visitor.status === "Rejected" ? "error.light" :
                                visitor.status === "On Hold" ? "warning.light" :
                                "info.light",
                        width: 32,
                        height: 32,
                        mr: 1,
                      }}
                    >
                      {visitor.full_name ? visitor.full_name.charAt(0).toUpperCase() : "V"}
                    </Avatar>
                    <Typography variant="body2" fontWeight="500">
                      {visitor.full_name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PhoneIcon 
                      fontSize="small" 
                      sx={{ mr: 1, color: "text.secondary", fontSize: "1rem" }} 
                    />
                    <Typography variant="body2">
                      {visitor.contact_number}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <EmailIcon 
                      fontSize="small" 
                      sx={{ mr: 1, color: "text.secondary", fontSize: "1rem" }} 
                    />
                    <Typography variant="body2">
                      {visitor.email}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CalendarTodayIcon 
                      fontSize="small" 
                      sx={{ mr: 1, color: "text.secondary", fontSize: "1rem" }} 
                    />
                    <Typography variant="body2">
                      {new Date(visitor.visit_from).toLocaleDateString()} - {" "}
                      {new Date(visitor.visit_till).toLocaleDateString()}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <StatusChip status={visitor.status} />
                </TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewDetails(visitor)}
                    size="small"
                    sx={{ 
                      borderRadius: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                      boxShadow: "none",
                      minWidth: "100px"
                    }}
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                <Box 
                  sx={{ 
                    display: "flex", 
                    flexDirection: "column", 
                    alignItems: "center",
                    p: 3
                  }}
                >
                  <Avatar 
                    sx={{ 
                      bgcolor: "grey.200",
                      width: 56,
                      height: 56,
                      mb: 2
                    }}
                  >
                    <InfoIcon fontSize="large" sx={{ color: "text.secondary" }} />
                  </Avatar>
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    No visitors found
                  </Typography>
                  <Typography variant="body2" color="textSecondary" align="center" sx={{ maxWidth: 400, mb: 2 }}>
                    {searchText 
                      ? "No visitors match your search criteria. Try a different search term or clear the search." 
                      : `No visitors found for the ${
                          currentTab === 0 ? "today" : 
                          currentTab === 1 ? "upcoming" :
                          currentTab === 2 ? "pending" : "past"
                        } category.`
                    }
                  </Typography>
                  {searchText && (
                    <Button 
                      variant="outlined" 
                      onClick={() => setSearchText("")}
                      sx={{ borderRadius: 2, textTransform: "none" }}
                    >
                      Clear Search
                    </Button>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {filteredVisitors.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={getFilteredVisitors(currentTab).length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </TableContainer>
  );

  // Mobile visitor list
  const VisitorList = ({ filteredVisitors }) => (
    <Box>
      {filteredVisitors.length > 0 ? (
        filteredVisitors.map((visitor) => (
          <VisitorCard key={visitor.id} visitor={visitor} />
        ))
      ) : (
        <Box 
          sx={{ 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center",
            p: 3,
            bgcolor: "white",
            borderRadius: 2,
            border: "1px solid rgba(0, 0, 0, 0.09)"
          }}
        >
          <Avatar 
            sx={{ 
              bgcolor: "grey.200",
              width: 56,
              height: 56,
              mb: 2
            }}
          >
            <InfoIcon fontSize="large" sx={{ color: "text.secondary" }} />
          </Avatar>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No visitors found
          </Typography>
          <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 2 }}>
            {searchText 
              ? "No visitors match your search criteria." 
              : `No visitors found for the ${
                  currentTab === 0 ? "today" : 
                  currentTab === 1 ? "upcoming" :
                  currentTab === 2 ? "pending" : "past"
                } category.`
            }
          </Typography>
          {searchText && (
            <Button 
              variant="outlined" 
              onClick={() => setSearchText("")}
              size="small"
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Clear Search
            </Button>
          )}
        </Box>
      )}
    </Box>
  );

  // Tab with badge for count
  const TabWithBadge = ({ label, count, icon, ...props }) => (
    <Tab
      icon={
        <Badge 
          badgeContent={count} 
          color="secondary"
          sx={{
            "& .MuiBadge-badge": {
              fontSize: "0.7rem",
              height: "20px",
              minWidth: "20px",
            }
          }}
        >
          {icon}
        </Badge>
      }
      label={!isMobile ? label : undefined}
      sx={{
        minHeight: { xs: 48, sm: 64 },
        minWidth: { xs: "auto", sm: 90, md: 120 },
        px: { xs: 1, sm: 2 },
        textTransform: "none",
        fontWeight: props.value === currentTab ? 600 : 400,
        fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" },
      }}
      {...props}
    />
  );

  return (
    <Box 
      sx={{ 
        backgroundColor: "#f5f7fa",
        minHeight: "100vh",
        py: { xs: 2, sm: 4, md: 6 },
        px: { xs: 2, sm: 3, md: 4 },
        mt: { xs: 10, sm: 4, md: 5 }, // Match the margin top from other views
      }}
    >
      <CBackDrop cOpen={loading} />
      <Container maxWidth="xl" sx={{ mb: 6 }}>
        <Card
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              backgroundImage: "linear-gradient(120deg, #0d47a1, #0d47a1)",
              color: "white",
              padding: { xs: "1.5em", sm: "2em" },
              position: "relative",
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                sx={{ 
                  fontWeight: "bold", 
                  letterSpacing: 1,
                  textShadow: "0px 2px 4px rgba(0,0,0,0.3)",
                }}
              >
                VISITOR APPLICATIONS
              </Typography>
              
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  mt: 1, 
                  opacity: 0.9,
                  fontWeight: 400,
                  letterSpacing: 0.5 
                }}
              >
                Manage and review visitor requests
              </Typography>
            </Box>
          </Box>

          {/* Tabs Navigation */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 0,
              backgroundColor: "#f8fafd",
              borderBottom: "1px solid rgba(0, 0, 0, 0.09)",
            }}
          >
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "fullWidth"}
              scrollButtons={isMobile ? "auto" : false}
              aria-label="Visitor Applications Tabs"
              sx={{
                "& .MuiTabs-indicator": {
                  height: 3,
                  borderTopLeftRadius: 3,
                  borderTopRightRadius: 3,
                },
              }}
            >
              <TabWithBadge 
                label="Today's Visitors" 
                count={tabCounts.today} 
                icon={<EventIcon />}
                value={0}
                id="tab-0"
                aria-controls="tabpanel-0"
              />
              <TabWithBadge 
                label="Upcoming Visitors" 
                count={tabCounts.upcoming} 
                icon={<EventAvailableIcon />}
                value={1}
                id="tab-1"
                aria-controls="tabpanel-1"
              />
              <TabWithBadge 
                label="Pending Requests" 
                count={tabCounts.pending} 
                icon={<ScheduleIcon />}
                value={2}
                id="tab-2"
                aria-controls="tabpanel-2"
              />
              <TabWithBadge 
                label="Past Requests" 
                count={tabCounts.past} 
                icon={<HistoryIcon />}
                value={3}
                id="tab-3"
                aria-controls="tabpanel-3"
              />
            </Tabs>
          </Paper>

          {/* Search & Refresh */}
          <Box 
            sx={{ 
              p: 2,
              display: "flex",
              alignItems: "center",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <TextField
              placeholder="Search visitors..."
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton 
                      size="small" 
                      onClick={() => setSearchText("")}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { borderRadius: 2 }
              }}
              sx={{ 
                width: { xs: "100%", sm: 300 },
              }}
            />
            
            <Tooltip title="Refresh Data">
              <Button
                startIcon={<RefreshIcon />}
                variant="outlined"
                onClick={refreshData}
                sx={{ 
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 500,
                  width: { xs: "100%", sm: "auto" }
                }}
              >
                Refresh
              </Button>
            </Tooltip>
          </Box>

          {/* Tab Panels */}
          <TabPanel value={currentTab} index={0}>
            {isMobile ? (
              <VisitorList filteredVisitors={getPaginatedVisitors(0)} />
            ) : (
              <VisitorTable filteredVisitors={getPaginatedVisitors(0)} />
            )}
          </TabPanel>
          <TabPanel value={currentTab} index={1}>
            {isMobile ? (
              <VisitorList filteredVisitors={getPaginatedVisitors(1)} />
            ) : (
              <VisitorTable filteredVisitors={getPaginatedVisitors(1)} />
            )}
          </TabPanel>
          <TabPanel value={currentTab} index={2}>
            {isMobile ? (
              <VisitorList filteredVisitors={getPaginatedVisitors(2)} />
            ) : (
              <VisitorTable filteredVisitors={getPaginatedVisitors(2)} />
            )}
          </TabPanel>
          <TabPanel value={currentTab} index={3}>
            {isMobile ? (
              <VisitorList filteredVisitors={getPaginatedVisitors(3)} />
            ) : (
              <VisitorTable filteredVisitors={getPaginatedVisitors(3)} />
            )}
          </TabPanel>
        </Card>
      </Container>
    </Box>
  );
};

export default ApproverDashboardView;